import { RootState } from '../store';
import { EGroupItemType, EGroupType, Group, GroupItem } from '../../models';
import { createSelector } from '@reduxjs/toolkit';
import {
  excludePlaceholderDefaultGroups,
  getActiveBlockingItems,
  getWhitelistAllowedItems,
} from '@/utils/helpers/groups';

export const selectGroups = (state: RootState): Group[] => state.groups;

export const selectAllGroupsItems = createSelector(
  [selectGroups],
  (groups: Group[]): GroupItem[] => groups.flatMap(group => group.items)
);

export const selectWhitelistAllowedItems = createSelector(
  [selectGroups],
  getWhitelistAllowedItems
);

export const selectActiveBlockingItems = createSelector(
  [selectGroups],
  getActiveBlockingItems
);

export const selectAllScheduleGroupsWebsites = createSelector(
  [selectGroups],
  (groups: Group[]): GroupItem[] => {
    const flatItems = groups
      .filter(g => g.type === EGroupType.schedule)
      .flatMap(group => group.items);
    return flatItems.filter(item => item.itemType === EGroupItemType.website);
  }
);

export const selectTotalItemsCount = (groupType: EGroupType) => {
  return createSelector([selectGroups], (groups: Group[]): number => {
    return groups
      .filter(g => g.type === groupType)
      .flatMap(group => group.items).length;
  });
};

/**
 * Excludes the placeholder default group (see `excludePlaceholderDefaultGroups`) so
 * every consumer of "the user's schedule groups" - rendering, group-limit counting,
 * empty-state detection - agrees on what counts as a real group.
 */
export const selectScheduleGroupsWithItems = createSelector(
  [selectGroups],
  (groups: Group[]): Group[] =>
    excludePlaceholderDefaultGroups(
      groups.filter(g => g.type === EGroupType.schedule)
    )
);

export const selectFirstScheduleGroup = (state: RootState) =>
  state.groups.find(g => g.type === EGroupType.schedule);

export const selectIsRulesEmptyState = createSelector(
  [selectScheduleGroupsWithItems],
  (scheduleGroups: Group[]): boolean => scheduleGroups.length === 0
);
